import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IntegrationDefinition } from '@/hooks/use-integrations';

interface IntegrationCardsProps {
  integrations: IntegrationDefinition[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  tenantCounts: Map<string, number>;
  totalTenants: number;
}

const COLLAPSED_COUNT = 8;

export function IntegrationCards({ integrations, selectedId, onSelect, tenantCounts, totalTenants }: IntegrationCardsProps) {
  const [expanded, setExpanded] = useState(false);

  const sorted = [...integrations]
    .filter(i => (tenantCounts.get(i.id) ?? 0) > 0)
    .sort((a, b) => (tenantCounts.get(b.id) ?? 0) - (tenantCounts.get(a.id) ?? 0));

  const visible = expanded ? sorted : sorted.slice(0, COLLAPSED_COUNT);
  const hasMore = sorted.length > COLLAPSED_COUNT;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => { onSelect(null); }}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer',
            selectedId === null
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-background text-foreground hover:bg-muted',
          )}
        >
          All
          <span className={cn(
            'rounded-full px-1.5 py-0.5 text-xs font-semibold',
            selectedId === null ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted-foreground/10 text-muted-foreground',
          )}>
            {totalTenants}
          </span>
        </button>
        {visible.map(integration => {
          const count = tenantCounts.get(integration.id) ?? 0;
          const isSelected = selectedId === integration.id;
          return (
            <button
              key={integration.id}
              type="button"
              onClick={() => { onSelect(isSelected ? null : integration.id); }}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer',
                isSelected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-foreground hover:bg-muted',
              )}
            >
              {integration.name}
              <span className={cn(
                'rounded-full px-1.5 py-0.5 text-xs font-semibold',
                isSelected ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted-foreground/10 text-muted-foreground',
              )}>
                {count}
              </span>
            </button>
          );
        })}
        {hasMore && (
          <button
            type="button"
            onClick={() => { setExpanded(!expanded); }}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted transition-colors cursor-pointer"
          >
            {expanded ? 'Show less' : `+${sorted.length - COLLAPSED_COUNT} more`}
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}
